'use client'

import Link from 'next/link'
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Apple,
  ArrowRight,
  Bot,
  Briefcase,
  Check,
  Coffee,
  LineChart,
  Link2,
  Scale,
  Sparkles,
  ArrowUp,
  Train,
  TrendingUp,
  Utensils,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { SignedIn, SignedOut } from '@clerk/nextjs'
import { BrandLockup, BrandMark, Rise, useCountUp, useInView } from './atoms'

// ── LEDGER STRIP ─────────────────────────────────────────────────
export function LedgerStrip() {
  const [ref, seen] = useInView<HTMLElement>({ threshold: 0.4 })
  const nw = useCountUp(127_432.18, { duration: 1800, decimals: 2 }, seen)
  const flow = useCountUp(3284.5, { duration: 1800, decimals: 2 }, seen)
  const save = useCountUp(32, { duration: 1600 }, seen)
  const run = useCountUp(18, { duration: 1600 }, seen)
  return (
    <section className="ledger" ref={ref} id="why">
      <div className="ledger-row">
        <div className="ledger-cell">
          <span className="ledger-label">Net worth</span>
          <span className="ledger-value">
            ${nw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="ledger-delta">+$4,128.42 · 30 days</span>
        </div>
        <div className="ledger-cell">
          <span className="ledger-label">Monthly cash flow</span>
          <span className="ledger-value">
            +${flow.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="ledger-delta">+12% MoM</span>
        </div>
        <div className="ledger-cell">
          <span className="ledger-label">Savings rate</span>
          <span className="ledger-value">{save}%</span>
          <span className="ledger-delta">+3 pts this quarter</span>
        </div>
        <div className="ledger-cell">
          <span className="ledger-label">Runway</span>
          <span className="ledger-value">{run} mo</span>
          <span className="ledger-delta">comfortable</span>
        </div>
      </div>
    </section>
  )
}

// ── FEATURE GRID ─────────────────────────────────────────────────
export function FeatureGrid() {
  return (
    <section id="features">
      <div className="shell">
        <Rise>
          <div className="features-head">
            <div>
              <div className="section-eyebrow">Built for serious money</div>
              <h2 className="section-title">A ledger that finally tells the truth.</h2>
            </div>
            <p className="section-sub" style={{ margin: 0 }}>
              Every Ribbit dollar has a debit and a credit. The math is honest, the reports are real,
              and your books actually balance — without you doing the accounting.
            </p>
          </div>
        </Rise>
        <Rise stagger threshold={0.1}>
          <div className="features-grid">
            <article className="feature">
              <span className="feature-mark">
                <Scale size={18} />
              </span>
              <h3>Double-entry, automatic</h3>
              <p>
                Every transaction posts a balanced journal entry. Your balance sheet, income
                statement, and cash flow are always live — never stitched together at year-end.
              </p>
              <div className="feature-art">
                <div className="frow">
                  <span className="l">
                    <span className="code">1010</span>Cash
                  </span>
                  <span className="v positive">+$6,420.00</span>
                </div>
                <div className="frow">
                  <span className="l">
                    <span className="code">4000</span>Salary
                  </span>
                  <span className="v" style={{ color: 'var(--muted-foreground)' }}>
                    −6,420.00
                  </span>
                </div>
                <div className="frow foot">
                  <span className="l">Δ</span>
                  <span className="v balanced">$0.00 · balanced</span>
                </div>
              </div>
            </article>

            <article className="feature">
              <span className="feature-mark">
                <Link2 size={18} />
              </span>
              <h3>Bank sync via Plaid</h3>
              <p>
                Connect checking, credit, brokerage, and 11,000 other institutions. Ribbit pulls the
                last two years of transactions and keeps everything fresh, automatically.
              </p>
              <div className="feature-art">
                {([
                  ['1010', 'Chase Checking', '$14,238.92', 'pos'],
                  ['2100', 'Amex Platinum', '−$1,842.14', ''],
                  ['1500', 'Public.com Broker', '$96,401.40', 'pos'],
                ] as const).map(([c, n, v, tone]) => (
                  <div key={n} className="frow">
                    <span className="l">
                      <span className="code">{c}</span>
                      {n}
                    </span>
                    <span className={`v ${tone === 'pos' ? 'positive' : ''}`}>{v}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className="feature">
              <span className="feature-mark">
                <LineChart size={18} />
              </span>
              <h3>Reports without a spreadsheet</h3>
              <p>
                Net worth over time, income vs expenses, savings rate, runway, category trends —
                pre-built reports that read straight from your live ledger.
              </p>
              <div className="feature-art">
                <ReportTrend />
              </div>
            </article>
          </div>
        </Rise>
      </div>
    </section>
  )
}

function ReportTrend() {
  const [ref, seen] = useInView<HTMLDivElement>({ threshold: 0.3 })
  const w = 320
  const h = 70
  const series = useMemo(() => {
    const arr: number[] = []
    for (let i = 0; i < 24; i++) {
      arr.push(40 + Math.sin(i * 0.7) * 8 + i * 1.2 + (i === 14 ? -4 : 0))
    }
    return arr
  }, [])
  const min = Math.min(...series)
  const max = Math.max(...series)
  const xs = series.map((_, i) => (i / (series.length - 1)) * w)
  const ys = series.map((v) => h - 8 - ((v - min) / (max - min)) * (h - 16))
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${ys[i].toFixed(1)}`).join(' ')
  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          YoY net worth
        </span>
        <span
          className="positive"
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--positive)',
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          +24.6%
        </span>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        height={56}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <path
          d={d}
          fill="none"
          stroke="var(--accent-9)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          pathLength={1}
          style={{
            strokeDasharray: 1,
            strokeDashoffset: seen ? 0 : 1,
            transition: 'stroke-dashoffset 1400ms cubic-bezier(0.2,0.7,0.2,1) 200ms',
          }}
        />
        <circle
          cx={xs[xs.length - 1]}
          cy={ys[ys.length - 1]}
          r="3"
          fill="var(--accent-9)"
          stroke="white"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  )
}

// ── BOOKKEEPING showcase ─────────────────────────────────────────
export function BookkeepingShow() {
  return (
    <section id="bookkeeping">
      <div className="shell">
        <div className="show">
          <Rise>
            <div className="show-copy">
              <div className="section-eyebrow">Double-entry, in plain sight</div>
              <h2>Books that balance themselves — because the math says so.</h2>
              <p>
                Drop a grocery receipt in, and two lines of accounting appear: cash goes down,
                groceries go up, totals reconcile. Run a balance sheet a second later and it reflects
                the change.
              </p>
              <ul className="show-list">
                {[
                  'Real debits and credits — not "categories"',
                  'Chart of accounts with 4-digit codes',
                  'Splits, transfers, opening balances, accruals',
                  'Live income statement, balance sheet, cash flow',
                ].map((t) => (
                  <li key={t}>
                    <span className="tick">
                      <Check size={11} />
                    </span>{' '}
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Rise>
          <Rise>
            <JournalCard />
          </Rise>
        </div>
      </div>
    </section>
  )
}

function JournalCard() {
  const [bal, setBal] = useState(false)
  const [ref, seen] = useInView<HTMLDivElement>({ threshold: 0.3 })
  useEffect(() => {
    if (!seen) return
    const t = setTimeout(() => setBal(true), 900)
    return () => clearTimeout(t)
  }, [seen])
  return (
    <div className="show-art" ref={ref}>
      <div className="show-art-head">
        <span className="show-art-title">journal-entry / 2026-05-14</span>
        <div className="show-art-dots">
          <span />
          <span />
          <span />
        </div>
      </div>
      <div className="show-art-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Trader Joe&rsquo;s</div>
            <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>May 14, 2026 · Posted</div>
          </div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 18,
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            −$84.31
          </div>
        </div>
        <table className="journal">
          <thead>
            <tr>
              <th>Account</th>
              <th className="r">Debit</th>
              <th className="r">Credit</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span className="code">5300</span>Groceries
              </td>
              <td className="r">$84.31</td>
              <td className="r" style={{ color: 'var(--muted-foreground)' }}>
                —
              </td>
            </tr>
            <tr>
              <td>
                <span className="code">2100</span>Amex Platinum
              </td>
              <td className="r" style={{ color: 'var(--muted-foreground)' }}>
                —
              </td>
              <td className="r">$84.31</td>
            </tr>
            <tr className="journal-foot">
              <td>Totals</td>
              <td className="r">$84.31</td>
              <td className="r">$84.31</td>
            </tr>
          </tbody>
        </table>
        <div
          className="balance-line"
          style={{
            opacity: bal ? 1 : 0,
            transform: bal ? 'translateY(0)' : 'translateY(4px)',
            transition: 'all 500ms ease',
          }}
        >
          <span className="pip">
            <Check size={11} />
          </span>
          Δ $0.00 — entry is balanced
        </div>
      </div>
    </div>
  )
}

// ── AI ASSISTANT showcase ────────────────────────────────────────
type AiNode =
  | { kind: 'user'; text: string; t: number }
  | { kind: 'tool'; text: string; t: number; durMs?: number }
  | { kind: 'ai'; type: string; t: number }

const AI_SCRIPT: AiNode[] = [
  { kind: 'user', text: 'How much did I spend on groceries this month?', t: 200 },
  { kind: 'tool', text: 'search_transactions(category="groceries", month="2026-05")', t: 800, durMs: 1200 },
  { kind: 'tool', text: 'compute_report(name="category_spend", group="month")', t: 2200, durMs: 1000 },
  {
    kind: 'ai',
    type: 'You spent **$612.40** on groceries in May across **9 transactions** — 17% below your 6-month average of $738. Trader Joe’s accounted for $284.06 of that.',
    t: 3500,
  },
]

export function AiShow() {
  return (
    <section
      id="ai"
      style={{
        background: 'var(--neutral-2)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="shell">
        <div className="show flip">
          <Rise>
            <div className="show-copy">
              <div className="section-eyebrow">AI assistant</div>
              <h2>Ask your books a question. Get a real answer.</h2>
              <p>
                Eight tool calls connect Claude directly to your ledger — search transactions, compute
                reports, run scenarios, even write a quick Python analysis. No hallucinated numbers,
                just receipts.
              </p>
              <ul className="show-list">
                {[
                  'Searches transactions and balances live',
                  'Pulls reports and explains the deltas',
                  'Runs what-if scenarios on your real numbers',
                  "Custom Python for the analyses we don't ship",
                ].map((t) => (
                  <li key={t}>
                    <span className="tick">
                      <Check size={11} />
                    </span>{' '}
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Rise>
          <Rise>
            <AiChatDemo />
          </Rise>
        </div>
      </div>
    </section>
  )
}

function AiChatDemo() {
  const [ref, seen] = useInView<HTMLDivElement>({ threshold: 0.3 })
  const [step, setStep] = useState(0)
  const [typed, setTyped] = useState('')
  const [toolDone, setToolDone] = useState<Record<number, boolean>>({})

  useEffect(() => {
    if (!seen) return
    const timers: ReturnType<typeof setTimeout>[] = []
    AI_SCRIPT.forEach((node, i) => {
      timers.push(setTimeout(() => setStep((s) => Math.max(s, i + 1)), node.t))
      if (node.kind === 'tool') {
        timers.push(
          setTimeout(() => setToolDone((d) => ({ ...d, [i]: true })), node.t + (node.durMs ?? 1000)),
        )
      }
    })
    const last = AI_SCRIPT[AI_SCRIPT.length - 1]
    if (last.kind === 'ai') {
      timers.push(
        setTimeout(() => {
          const text = last.type
          let i = 0
          const tick = () => {
            i += 1
            setTyped(text.slice(0, i))
            if (i < text.length) timers.push(setTimeout(tick, 14 + Math.random() * 14))
          }
          tick()
        }, last.t + 200),
      )
    }
    return () => timers.forEach(clearTimeout)
  }, [seen])

  const renderText = (t: string) => {
    const parts = t.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((p, i) =>
      p.startsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>,
    )
  }

  return (
    <div className="show-art" ref={ref}>
      <div className="show-art-head">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: 'var(--accent-3)',
              color: 'var(--accent-11)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Bot size={13} />
          </span>
          <span className="show-art-title">ai · /ai</span>
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>claude · sonnet</span>
      </div>
      <div className="chat">
        <div className="chat-stream">
          {AI_SCRIPT.slice(0, step).map((n, i) => {
            if (n.kind === 'user') return <div key={i} className="bubble user">{n.text}</div>
            if (n.kind === 'tool')
              return (
                <div key={i} className={`tool-call ${toolDone[i] ? 'done' : ''}`}>
                  <span className="spin" />
                  {toolDone[i] ? <Check size={12} /> : null}
                  {n.text}
                </div>
              )
            if (n.kind === 'ai')
              return (
                <div key={i} className="bubble ai">
                  {renderText(typed)}
                  {typed.length < n.type.length ? <span className="cursor" /> : null}
                </div>
              )
            return null
          })}
          {step === 0 && seen && (
            <div className="bubble ai" style={{ color: 'var(--muted-foreground)' }}>
              <span className="typing-dots">
                <span />
                <span />
                <span />
              </span>
            </div>
          )}
        </div>
        <div className="chat-input">
          <Sparkles size={14} style={{ color: 'var(--accent-11)' }} />
          <input placeholder="Ask anything about your money…" readOnly value="" />
          <button aria-label="Send">
            <ArrowUp size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}

// ── TRANSACTIONS preview ─────────────────────────────────────────
type TxRow = {
  d: string
  merchant: string
  acc: string
  cat: string
  ic: LucideIcon
  tone: '' | 'warm' | 'blue' | 'purple' | 'gray'
  amt: number
}
const TX_DATA: TxRow[] = [
  { d: 'May 14', merchant: 'Trader Joe’s', acc: '2100 Amex', cat: 'Groceries', ic: Utensils, tone: 'warm', amt: -84.31 },
  { d: 'May 13', merchant: 'Acme Inc.', acc: '1010 Chase', cat: 'Salary', ic: Briefcase, tone: 'blue', amt: 6420.0 },
  { d: 'May 13', merchant: 'Atlas Coffee', acc: '2100 Amex', cat: 'Dining', ic: Coffee, tone: 'warm', amt: -6.25 },
  { d: 'May 12', merchant: 'Public.com', acc: '1500 Brokerage', cat: 'Dividend', ic: TrendingUp, tone: '', amt: 18.42 },
  { d: 'May 12', merchant: 'Caltrain', acc: '2100 Amex', cat: 'Transport', ic: Train, tone: 'blue', amt: -10.5 },
  { d: 'May 11', merchant: 'PG&E', acc: '1010 Chase', cat: 'Utilities', ic: Zap, tone: 'purple', amt: -142.18 },
  { d: 'May 11', merchant: 'Whole Foods', acc: '2100 Amex', cat: 'Groceries', ic: Utensils, tone: 'warm', amt: -68.94 },
  { d: 'May 10', merchant: 'Apple.com', acc: '2100 Amex', cat: 'Subscriptions', ic: Apple, tone: 'gray', amt: -9.99 },
]

export function TxShow() {
  const [ref, seen] = useInView<HTMLDivElement>({ threshold: 0.15 })
  return (
    <section id="transactions">
      <div className="shell">
        <div className="show">
          <Rise>
            <div className="show-art" ref={ref}>
              <div className="show-art-head">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Transactions{' '}
                  <span style={{ fontSize: 12, color: 'var(--muted-foreground)', fontWeight: 400 }}>
                    · May 2026
                  </span>
                </span>
                <div className="mini-pillrow">
                  <span className="mini-pill on">All</span>
                  <span className="mini-pill">Income</span>
                  <span className="mini-pill">Expense</span>
                </div>
              </div>
              <div className="show-art-body" style={{ padding: 0 }}>
                <table className="tx-table">
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Date</th>
                      <th>Description</th>
                      <th>Category</th>
                      <th>Account</th>
                      <th className="r">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TX_DATA.map((t, i) => {
                      const Ic = t.ic
                      return (
                        <tr
                          key={i}
                          className={`tx-row ${seen ? 'in' : ''}`}
                          style={{ transitionDelay: `${80 + i * 70}ms` }}
                        >
                          <td
                            style={{
                              color: 'var(--muted-foreground)',
                              fontFamily: 'var(--font-mono)',
                              fontSize: 12,
                            }}
                          >
                            {t.d}
                          </td>
                          <td style={{ fontWeight: 500 }}>{t.merchant}</td>
                          <td>
                            <span className="tx-cat">
                              <span className={`cat-ic ${t.tone}`}>
                                <Ic size={12} />
                              </span>
                              {t.cat}
                            </span>
                          </td>
                          <td className="tx-acc">{t.acc}</td>
                          <td className={`tx-amt ${t.amt >= 0 ? 'pos' : ''}`}>
                            {t.amt >= 0
                              ? `+$${t.amt.toFixed(2)}`
                              : `−$${Math.abs(t.amt).toFixed(2)}`}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </Rise>
          <Rise>
            <div className="show-copy">
              <div className="section-eyebrow">Transactions</div>
              <h2>Dense, fast, opinionated about what you own.</h2>
              <p>
                Ribbit imports the last two years on connect, categorizes intelligently, and lets you
                split, transfer, or recategorize without breaking the ledger. The table is
                keyboard-first because finance is keyboard work.
              </p>
              <ul className="show-list">
                {[
                  'Lucide-coded categories, color-paired',
                  'Split a charge across multiple accounts',
                  'Transfers reconcile automatically',
                  'Tabular monospace amounts — columns always line up',
                ].map((t) => (
                  <li key={t}>
                    <span className="tick">
                      <Check size={11} />
                    </span>{' '}
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Rise>
        </div>
      </div>
    </section>
  )
}

// ── PLANNING ─────────────────────────────────────────────────────
export function PlanningShow() {
  return (
    <section
      id="planning"
      style={{
        background: 'var(--neutral-2)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="shell">
        <div className="show flip">
          <Rise>
            <div className="show-copy">
              <div className="section-eyebrow">Scenarios</div>
              <h2>What if you bought the house? Model it in seconds.</h2>
              <p>
                The planning grid runs your real ledger forward, then lets you fork it. Buy a $35k
                car on a 5-year loan, increase rent by 8%, max your 401(k) — see runway, net worth,
                and cash flow rewrite themselves.
              </p>
              <ul className="show-list">
                {[
                  'Branch scenarios from any month',
                  'Compare side-by-side against baseline',
                  'Sensitivity sliders on every assumption',
                ].map((t) => (
                  <li key={t}>
                    <span className="tick">
                      <Check size={11} />
                    </span>{' '}
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Rise>
          <Rise>
            <PlanningGrid />
          </Rise>
        </div>
      </div>
    </section>
  )
}

function PlanningGrid() {
  const cols = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov']
  const rows: { name: string; vals: number[]; strong?: boolean }[] = [
    { name: 'Income', vals: [6420, 6420, 6420, 6420, 7100, 7100] },
    { name: 'Housing', vals: [-2400, -2400, -2400, -2400, -2400, -2592] },
    { name: 'Auto loan', vals: [0, -680, -680, -680, -680, -680] },
    { name: 'Other', vals: [-1840, -1840, -1840, -1840, -1920, -1920] },
    { name: 'Net', vals: [2180, 1500, 1500, 1500, 2100, 1908], strong: true },
  ]
  return (
    <div className="plan-grid">
      <div className="ph">Category</div>
      {cols.map((c) => (
        <div key={c} className="ph" style={{ textAlign: 'right' }}>
          {c}
        </div>
      ))}
      {rows.map((r) => (
        <Fragment key={r.name}>
          <div
            className="pr"
            style={{
              fontWeight: r.strong ? 600 : 500,
              background: r.strong ? 'var(--neutral-2)' : undefined,
            }}
          >
            {r.name}
          </div>
          {r.vals.map((v, i) => (
            <div
              key={i}
              className={`pcell ${v < 0 ? 'neg' : v > 0 ? 'pos' : ''}`}
              style={{
                background: r.strong ? 'var(--neutral-2)' : undefined,
                fontWeight: r.strong ? 600 : undefined,
              }}
            >
              {v === 0 ? '—' : v > 0 ? `+$${v.toLocaleString()}` : `−$${Math.abs(v).toLocaleString()}`}
            </div>
          ))}
        </Fragment>
      ))}
    </div>
  )
}

// ── PRICING ──────────────────────────────────────────────────────
export function Pricing() {
  return (
    <section id="pricing">
      <div className="shell">
        <Rise>
          <div
            style={{
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
              marginBottom: 56,
            }}
          >
            <span className="section-eyebrow">Pricing</span>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Know your money.
            </h2>
            <p className="section-sub" style={{ textAlign: 'center', margin: 0 }}>
              Everything you need to track, understand, and plan your finances. Upgrade when you want
              AI on your side.
            </p>
          </div>
        </Rise>
        <Rise>
          <div className="pricing-row">
            <div className="plan">
              <div className="plan-head">
                <span className="plan-name">Free</span>
              </div>
              <p className="plan-tagline">Full-featured personal finance, no strings attached.</p>
              <div className="plan-price">
                <span className="amt">$0</span>
                <span className="per">/forever</span>
              </div>
              <ul className="plan-feat">
                {[
                  'Double-entry bookkeeping',
                  'Unlimited accounts and transactions',
                  'Bank sync via Plaid',
                  'Reports and dashboards',
                  'Investment tracking',
                  'Scenario planning',
                ].map((f) => (
                  <li key={f}>
                    <span className="tick">
                      <Check size={11} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <SignedOut>
                <Link className="btn btn-outline btn-lg" style={{ marginTop: 8 }} href="/sign-up">
                  Start free
                </Link>
              </SignedOut>
              <SignedIn>
                <Link className="btn btn-outline btn-lg" style={{ marginTop: 8 }} href="/dashboard">
                  Go to dashboard
                </Link>
              </SignedIn>
            </div>
            <div className="plan pro">
              <div className="plan-head">
                <span className="plan-name">Pro</span>
                <span className="plan-badge">AI included</span>
              </div>
              <p className="plan-tagline">AI-powered insights to make smarter decisions.</p>
              <div className="plan-price">
                <span className="amt">$6</span>
                <span className="per">/month</span>
              </div>
              <span className="billed-note">$72 billed annually · $8 month-to-month</span>
              <ul className="plan-feat">
                {[
                  'Everything in Free',
                  'AI assistant with eight tool calls',
                  'Custom Python analyses',
                  'What-if scenarios on real data',
                  'Priority support',
                ].map((f) => (
                  <li key={f}>
                    <span className="tick">
                      <Check size={11} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <SignedOut>
                <Link className="btn btn-primary btn-lg" style={{ marginTop: 8 }} href="/sign-up">
                  Upgrade to Pro{' '}
                  <span className="arr">
                    <ArrowRight size={14} />
                  </span>
                </Link>
              </SignedOut>
              <SignedIn>
                <Link className="btn btn-primary btn-lg" style={{ marginTop: 8 }} href="/settings/billing">
                  Manage subscription{' '}
                  <span className="arr">
                    <ArrowRight size={14} />
                  </span>
                </Link>
              </SignedIn>
            </div>
          </div>
        </Rise>
      </div>
    </section>
  )
}

// ── FINAL CTA ────────────────────────────────────────────────────
export function FinalCta() {
  return (
    <section className="final">
      <div className="final-bg" />
      <div className="shell">
        <Rise>
          <div className="final-inner">
            <BrandMark size={56} />
            <h2>
              Ready to <span className="ital">take control?</span>
            </h2>
            <p className="section-sub" style={{ textAlign: 'center', margin: 0 }}>
              Connect your first account in under two minutes. The dashboard fills in from there.
            </p>
            <div className="hero-ctas">
              <SignedOut>
                <Link className="btn btn-primary btn-lg" href="/sign-up">
                  Get started{' '}
                  <span className="arr">
                    <ArrowRight size={14} />
                  </span>
                </Link>
              </SignedOut>
              <SignedIn>
                <Link className="btn btn-primary btn-lg" href="/dashboard">
                  Go to dashboard{' '}
                  <span className="arr">
                    <ArrowRight size={14} />
                  </span>
                </Link>
              </SignedIn>
              <a className="btn btn-outline btn-lg" href="#pricing">
                See pricing
              </a>
            </div>
          </div>
        </Rise>
      </div>
    </section>
  )
}

// ── FOOTER ───────────────────────────────────────────────────────
export function Footer() {
  return (
    <footer>
      <div className="shell">
        <div className="foot-row">
          <div className="foot-brand">
            <BrandLockup markSize={26} wordSize={19} />
            <p>
              Modern double-entry bookkeeping for personal finance. Bank sync, AI analysis, scenario
              planning.
            </p>
          </div>
          <div className="foot-col">
            <h4>Product</h4>
            <ul>
              <li>
                <a href="#features">Features</a>
              </li>
              <li>
                <a href="#ai">AI assistant</a>
              </li>
              <li>
                <a href="#planning">Planning</a>
              </li>
              <li>
                <a href="#pricing">Pricing</a>
              </li>
            </ul>
          </div>
          <div className="foot-col">
            <h4>Company</h4>
            <ul>
              <li>
                <a href="#">About</a>
              </li>
              <li>
                <a href="#">Changelog</a>
              </li>
              <li>
                <a href="#">Blog</a>
              </li>
              <li>
                <a href="#">Contact</a>
              </li>
            </ul>
          </div>
          <div className="foot-col">
            <h4>Legal</h4>
            <ul>
              <li>
                <Link href="/privacy">Privacy policy</Link>
              </li>
              <li>
                <Link href="/terms">Terms of service</Link>
              </li>
              <li>
                <a href="#">Security</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="foot-base">
          <span>© {new Date().getFullYear()} Ribbit. All rights reserved.</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            ribbit. make more money.
          </span>
        </div>
      </div>
    </footer>
  )
}
